import { classnames } from '@/helpers';
import { _s } from '@/locale';
import { ChangeEvent, Dispatch, useEffect, useReducer, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import css from './PinInput.module.scss';

const isValidDigit = (input: string | number) => !isNaN(Number(input)) && /^[0-9\b]+$/.test('' + input);

type UsePinInputProps = {
  fields?: 4 | 6;
  validateCallback: (pin: string[]) => Promise<boolean>;
};

type PinInputState = {
  pin: string[];
  activePinIndex: number | undefined;
  error: boolean;
  submitting: boolean;
  clipError: boolean;
};

type PinInputAction =
  | { type: 'SET_PIN'; payload: { pin: string[] } }
  | { type: 'SET_ACTIVE_PIN_INDEX'; payload: { index?: number } }
  | { type: 'SET_ERROR'; payload: { error: boolean } }
  | { type: 'SET_SUBMITTING'; payload: { submitting: boolean } }
  | { type: 'SET_CLIP_ERROR'; payload: { clipError: boolean } };

const pinInputReducer = (state: PinInputState, action: PinInputAction): PinInputState => {
  switch (action.type) {
    case 'SET_PIN': {
      const { pin } = action.payload;
      return { ...state, pin };
    }
    case 'SET_ACTIVE_PIN_INDEX': {
      const { index } = action.payload;
      const activePinIndex = Math.max(Math.min(index, state.pin.length - 1), 0);
      return { ...state, activePinIndex };
    }
    case 'SET_ERROR': {
      const { error } = action.payload;
      return { ...state, error };
    }
    case 'SET_SUBMITTING': {
      const { submitting } = action.payload;
      return { ...state, submitting };
    }
    case 'SET_CLIP_ERROR': {
      const { clipError } = action.payload;
      return { ...state, clipError };
    }
  }
};

export type UsePinInputResult = ReturnType<typeof usePinInput>;

function resetPinInput(fields: UsePinInputProps['fields'], dispatch: Dispatch<PinInputAction>): void {
  dispatch({ type: 'SET_PIN', payload: { pin: Array.from({ length: fields }, () => '') } });
}

export const usePinInput = ({ fields = 4, validateCallback }: UsePinInputProps) => {
  const pinContainerRef = useRef<HTMLDivElement>();
  const inputRef = useRef<HTMLInputElement>();
  const [state, dispatch] = useReducer(pinInputReducer, {
    pin: Array.from({ length: fields }, () => ''),
    activePinIndex: 0,
    error: false,
    submitting: false,
    clipError: false,
  });

  useEffect(() => {
    const handleValidate = async () => {
      dispatch({ type: 'SET_SUBMITTING', payload: { submitting: true } });

      const isValid = await validateCallback(state.pin);
      if (!isValid) {
        dispatch({ type: 'SET_ERROR', payload: { error: true } });
        dispatch({ type: 'SET_SUBMITTING', payload: { submitting: false } });
        resetPinInput(fields, dispatch);
        dispatch({ type: 'SET_ACTIVE_PIN_INDEX', payload: { index: undefined } });
        inputRef.current?.blur();
      }
    };

    if (state.pin.every((input) => input)) {
      handleValidate();
    }
  }, [state.pin]);

  useEffect(() => {
    resetPinInput(fields, dispatch);
  }, [fields]);

  useEffect(() => {
    if (state.activePinIndex === fields) {
      dispatch({ type: 'SET_ACTIVE_PIN_INDEX', payload: { index: fields - 1 } });
    }
    if (state.activePinIndex < 0) {
      dispatch({ type: 'SET_ACTIVE_PIN_INDEX', payload: { index: 0 } });
    }

    inputRef.current?.focus();
  }, [state.activePinIndex]);

  const handlePinChange = ({ key }: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    const updated = [...state.pin];
    if (key === 'Backspace') {
      handleBackspace(index);
      return;
    }

    if (!isValidDigit(key)) return;

    updated[index] = key;
    dispatch({ type: 'SET_PIN', payload: { pin: updated } });
    dispatch({ type: 'SET_ACTIVE_PIN_INDEX', payload: { index: index + 1 } });
    dispatch({ type: 'SET_ERROR', payload: { error: false } });
  };

  const handleBackspace = (index) => {
    dispatch({ type: 'SET_ACTIVE_PIN_INDEX', payload: { index: index - 1 } });
    const updated = [...state.pin];
    updated[index] = '';
    dispatch({ type: 'SET_PIN', payload: { pin: updated } });
  };

  const handleFocus = (index: number) => {
    dispatch({ type: 'SET_ACTIVE_PIN_INDEX', payload: { index } });
    dispatch({ type: 'SET_ERROR', payload: { error: false } });
    dispatch({ type: 'SET_CLIP_ERROR', payload: { clipError: false } });
  };

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.length >= 6) {
      handlePaste(value);
      e.target.value = '';
    }
  };

  const handlePaste = (clipboard: string) => {
    if (!clipboard) return;

    const splitClipboard = clipboard.split('');

    if (splitClipboard.length !== fields || !splitClipboard.every(isValidDigit)) {
      dispatch({ type: 'SET_CLIP_ERROR', payload: { clipError: true } });
      return;
    }

    dispatch({ type: 'SET_PIN', payload: { pin: splitClipboard } });
    dispatch({ type: 'SET_ACTIVE_PIN_INDEX', payload: { index: state.pin.length } });
  };

  return {
    pin: state.pin,
    activePinIndex: state.activePinIndex,
    error: state.error,
    submitting: state.submitting,
    clipError: state.clipError,
    pinContainerRef,
    inputRef,
    handlePinChange,
    handleFocus,
    handleOnChange,
    handlePaste,
  };
};

const PinInput = ({
  activePinIndex,
  clipError,
  error,
  inputRef,
  pin,
  pinContainerRef,
  submitting,
  handleFocus,
  handlePaste,
  handleOnChange,
  handlePinChange,
}: ReturnType<typeof usePinInput>) => {
  return (
    <div>
      <div className="w-full" ref={pinContainerRef}>
        <div className="flex w-full flex-row gap-[6px]">
          {pin.map((_, index) => (
            <div
              key={index}
              className={twMerge(
                classnames(
                  index === activePinIndex
                    ? 'outline-border_focus border-border_regular outline outline-[3px]'
                    : 'border-border_regular',
                  ' bg-black-100 relative flex h-[78px] flex-1 items-center justify-center rounded-md border-[1px] text-[58px]',
                  css.pin,
                  error && 'border-border_negative',
                ),
              )}>
              <input
                className={`spin-button-none relative m-0 flex h-full w-full max-w-[80px] items-center justify-center bg-transparent text-transparent caret-transparent outline-none`}
                id={`pin-${index}`}
                ref={index === activePinIndex ? inputRef : null}
                type="text"
                pattern="[0-9]*"
                inputMode="numeric"
                maxLength={1}
                autoComplete="one-time-code"
                disabled={submitting}
                max={9}
                data-valid={!!pin[index]}
                onKeyDown={(e) => handlePinChange(e, index)}
                autoFocus={index === activePinIndex}
                onFocus={() => handleFocus(index)}
                onPasteCapture={(e) => handlePaste(e.clipboardData.getData('text'))}
                onChange={handleOnChange}
                defaultValue={pin[index] || ''}
                data-cy={`login-pin-input-${index}`}
              />
              <label
                className={`pointer-events-none absolute m-0 flex h-full w-full items-center justify-center ${
                  error || clipError ? 'text-fg_negative' : 'text-fg_primary'
                }`}
                htmlFor={`pin-${index}`}>
                {pin[index] || ''}
              </label>
            </div>
          ))}
        </div>
      </div>
      {(error || clipError) && (
        <span className="text-fg_negative text-xxs mt-md inline-block">{_s('PinInput.error')}</span>
      )}
    </div>
  );
};

export default PinInput;
